/**
 * An OSRS-style chat box, built from scratch — neither InfernoTrainer nor osrs-sdk has one.
 *
 * DOM rather than canvas, for three reasons:
 *   - every engine mouse listener is bound to the canvas, so a DOM overlay swallows its own
 *     clicks: picking an answer cannot also walk the player or open the context menu;
 *   - nothing in either project sets a z-index, so a fixed-position element appended after
 *     the canvas simply paints on top (the three.js Stats panel already relies on this);
 *   - the canvas has no word-wrap or hit-testing primitives, so questions would mean
 *     hand-rolled measureText layout.
 *
 * The OSRS look is close to free: `font-family: OSRS` and `color: #FFFF00` are already
 * global on body from index.html.
 */
import { LETTERS, type Letter, type Question } from "./constants";

/** Width reserved at the right edge for the engine's canvas-drawn control panel. */
const CONTROL_PANEL_GUTTER = 250;

const STYLE = `
/* Upstream's DOM sidebar carries a wave picker, a loadout picker and a pause button —
   all of which would let you leave the Zuk fight. It also overlaps the chatbox. The
   elements have to stay in the DOM (InfernoRegion.initialiseRegion() looks several of
   them up without null-guarding), so hide rather than remove. */
#right_panel, #disclaimer_panel { display: none !important; }

#trivia-chatbox {
  position: fixed;
  left: 0;
  bottom: 0;
  right: ${CONTROL_PANEL_GUTTER}px;
  max-height: 42vh;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 10px 14px 12px;
  background: rgba(0, 0, 0, 0.86);
  border-top: 2px solid #5a4b2a;
  font-family: 'OSRS', monospace;
  color: #ffff00;
  font-size: 20px;
  line-height: 1.35;
  user-select: none;
}
#trivia-chatbox .tc-status {
  display: flex;
  gap: 18px;
  color: #c8b273;
  font-size: 16px;
  margin-bottom: 4px;
}
#trivia-chatbox .tc-status .tc-hint { margin-left: auto; }
#trivia-chatbox .tc-question { color: #ffffff; margin-bottom: 6px; }
#trivia-chatbox .tc-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 18px;
}
#trivia-chatbox .tc-option {
  display: flex;
  gap: 8px;
  align-items: baseline;
  padding: 3px 6px;
  background: none;
  border: 1px solid transparent;
  color: #ffff00;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
#trivia-chatbox .tc-option:hover:not(:disabled) { background: #2a2415; border-color: #5a4b2a; }
#trivia-chatbox .tc-option:disabled { cursor: default; }
#trivia-chatbox .tc-option .tc-key { color: #c8b273; }
#trivia-chatbox .tc-option.tc-correct { color: #00ff00; }
#trivia-chatbox .tc-option.tc-wrong { color: #ff3333; }
#trivia-chatbox .tc-option.tc-dim { opacity: 0.45; }
#trivia-chatbox .tc-feedback { margin-top: 6px; min-height: 1.35em; }
#trivia-chatbox .tc-feedback.tc-good { color: #00ff00; }
#trivia-chatbox .tc-feedback.tc-bad { color: #ff3333; }
/* Persistent banner — the engine gives no other cue that Zuk sped up. */
#trivia-chatbox .tc-notice { color: #ff6a2b; }
#trivia-chatbox .tc-notice:empty { display: none; }
@media (max-width: 900px) {
  #trivia-chatbox { font-size: 16px; right: 0; }
  #trivia-chatbox .tc-options { grid-template-columns: minmax(0, 1fr); }
  #trivia-chatbox .tc-status .tc-hint { display: none; }
}
`;

export class Chatbox {
  private root: HTMLDivElement;
  private statusEl: HTMLDivElement;
  private questionEl: HTMLDivElement;
  private optionsEl: HTMLDivElement;
  private feedbackEl: HTMLDivElement;
  private noticeEl: HTMLDivElement;
  private buttons: Partial<Record<Letter, HTMLButtonElement>> = {};
  private locked = true;

  /** Set by the controller; receives the letter the player picked. */
  onAnswer: (letter: Letter) => void = () => undefined;

  constructor() {
    const style = document.createElement("style");
    style.textContent = STYLE;
    document.head.appendChild(style);

    this.root = document.createElement("div");
    this.root.id = "trivia-chatbox";

    this.statusEl = document.createElement("div");
    this.statusEl.className = "tc-status";

    this.questionEl = document.createElement("div");
    this.questionEl.className = "tc-question";

    this.optionsEl = document.createElement("div");
    this.optionsEl.className = "tc-options";

    this.feedbackEl = document.createElement("div");
    this.feedbackEl.className = "tc-feedback";

    this.noticeEl = document.createElement("div");
    this.noticeEl.className = "tc-notice";

    for (const letter of LETTERS) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "tc-option";
      button.dataset.letter = letter;

      const key = document.createElement("span");
      key.className = "tc-key";
      key.textContent = `${LETTERS.indexOf(letter) + 1}.`;

      const text = document.createElement("span");
      text.className = "tc-text";

      button.append(key, text);
      button.addEventListener("click", () => this.pick(letter));
      this.optionsEl.appendChild(button);
      this.buttons[letter] = button;
    }

    this.root.append(
      this.statusEl,
      this.noticeEl,
      this.questionEl,
      this.optionsEl,
      this.feedbackEl,
    );
    document.body.appendChild(this.root);

    // Keys 1-4 answer. They collide with nothing upstream: the engine binds F1-F5 for the
    // control-panel tabs and WASD/arrows for the 3D camera. stopPropagation anyway, so a
    // keypress meant for us never reaches Viewport3d's window-level handler.
    window.addEventListener("keydown", (event) => {
      const index = ["1", "2", "3", "4"].indexOf(event.key);
      if (index === -1 || this.locked) return;
      event.preventDefault();
      event.stopPropagation();
      this.pick(LETTERS[index]);
    });
  }

  private pick(letter: Letter): void {
    if (this.locked) return;
    this.locked = true;
    this.onAnswer(letter);
  }

  setStatus(correct: number, wrong: number, toKill: number): void {
    this.statusEl.innerHTML = "";
    const progress = document.createElement("span");
    progress.textContent = `Correct ${correct}/${toKill}`;
    const misses = document.createElement("span");
    misses.textContent = `Wrong ${wrong}`;
    const hint = document.createElement("span");
    hint.className = "tc-hint";
    hint.textContent = "Press 1-4 or click. Click the ground to move.";
    this.statusEl.append(progress, misses, hint);
  }

  showQuestion(question: Question): void {
    this.questionEl.textContent = question.question;
    const texts: Record<Letter, string> = {
      A: question.a,
      B: question.b,
      C: question.c,
      D: question.d,
    };
    for (const letter of LETTERS) {
      const button = this.buttons[letter];
      if (!button) continue;
      button.className = "tc-option";
      button.disabled = false;
      (button.querySelector(".tc-text") as HTMLSpanElement).textContent = texts[letter];
    }
    this.feedbackEl.className = "tc-feedback";
    this.feedbackEl.textContent = "";
    this.locked = false;
  }

  /** Colour the options after grading, then say what happened. */
  reveal(selected: Letter, correct: Letter, damage: number): void {
    for (const letter of LETTERS) {
      const button = this.buttons[letter];
      if (!button) continue;
      button.disabled = true;
      if (letter === correct) button.classList.add("tc-correct");
      else if (letter === selected) button.classList.add("tc-wrong");
      else button.classList.add("tc-dim");
    }
    const hit = selected === correct;
    this.feedbackEl.className = `tc-feedback ${hit ? "tc-good" : "tc-bad"}`;
    this.feedbackEl.textContent = hit
      ? `Correct! TzKal-Zuk takes ${damage} damage.`
      : `Wrong. The answer was ${correct}.`;
  }

  /** A line that persists across questions, unlike the per-answer feedback. */
  setNotice(message: string): void {
    this.noticeEl.textContent = message;
  }

  /** End of run — replace the question area with the outcome. */
  finish(message: string, good: boolean): void {
    this.locked = true;
    this.questionEl.textContent = "";
    this.optionsEl.innerHTML = "";
    this.noticeEl.textContent = "";
    this.feedbackEl.className = `tc-feedback ${good ? "tc-good" : "tc-bad"}`;
    this.feedbackEl.textContent = message;
  }

  say(message: string): void {
    this.feedbackEl.className = "tc-feedback";
    this.feedbackEl.textContent = message;
  }
}
